// Kétnyelvű (hu/en) űrlap-mező-pár — „zászló a mezőben" minta. EGYETLEN közös csoport-címke (pl. „Név") a pár
// FÖLÖTT; a nyelvet a mező-BEN lévő zászló jelöli (adornment). A zászló tisztán DEKORATÍV (`aria-hidden`), ezért
// MINDEN kontroll explicit `aria-label`-t kap a teljes névvel (`${label} ${langSuffix}` → „Név (magyar)"/
// „Név (angol)"), így a két mező akadálymentes neve EGYEDI marad és a `getByLabelText` a teljes névre illeszt.
// A látható csoport-fejléc NEM `<label>` (nem `htmlFor`-ol egyetlen kontrollra sem — nincs dupla felolvasás).
// Mindkét kontroll SAJÁT `FormField`-ben → a `FormMessage` + `aria-invalid` RHF-wiring mezőnként megmarad.
import { type FieldPath, type FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LangFlag } from '@/components/shared/lang-flag';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface BilingualFieldProps<TValues extends FieldValues> {
  /** Közös csoport-címke (a nyelv-független mező-név, pl. „Név"/„Leírás"). */
  label: string;
  required?: boolean;
  /** A magyar érték RHF-mező-útja. */
  nameHu: FieldPath<TValues>;
  /** Az angol érték RHF-mező-útja. */
  nameEn: FieldPath<TValues>;
  variant: 'input' | 'textarea';
  /** textarea sor-magasság (csak `variant="textarea"`). */
  rows?: number;
  /** input típusa (csak `variant="input"`). */
  type?: 'text' | 'email' | 'tel';
  placeholder?: string;
}

/** Kétnyelvű mező-pár egyetlen csoport-címkével, a nyelvet a mezőben lévő (dekoratív) zászló jelöli. */
export function BilingualField<TValues extends FieldValues>({
  label,
  required,
  nameHu,
  nameEn,
  variant,
  rows = 2,
  type = 'text',
  placeholder,
}: BilingualFieldProps<TValues>): JSX.Element {
  const { t } = useTranslation();

  /** Egy nyelvi kontroll — saját FormField (hibaüzenet + aria-invalid), a mezőben dekoratív zászlóval. */
  const renderControl = (lang: 'hu' | 'en', name: FieldPath<TValues>): JSX.Element => {
    const ariaLabel = `${label} ${t(`common.langSuffix.${lang}`)}`;
    return (
      <FormField<TValues>
        name={name}
        render={({ field }) => (
          <FormItem>
            <div className="relative">
              {variant === 'input' ? (
                <>
                  <LangFlag
                    lang={lang}
                    decorative
                    className="pointer-events-none absolute left-2.5 top-1/2 z-10 -translate-y-1/2"
                  />
                  <FormControl>
                    <Input
                      type={type}
                      placeholder={placeholder}
                      aria-label={ariaLabel}
                      className="pl-8"
                      {...field}
                      value={typeof field.value === 'string' ? field.value : ''}
                    />
                  </FormControl>
                </>
              ) : (
                <>
                  <LangFlag
                    lang={lang}
                    decorative
                    className="pointer-events-none absolute left-2.5 top-2 z-10"
                  />
                  <FormControl>
                    <Textarea
                      rows={rows}
                      placeholder={placeholder}
                      aria-label={ariaLabel}
                      className="pt-8"
                      {...field}
                      value={typeof field.value === 'string' ? field.value : ''}
                    />
                  </FormControl>
                </>
              )}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  return (
    <div className="space-y-1.5">
      {/* Csoport-fejléc: FormLabel-stílus, DE nem <label> (nem htmlFor-ol — az egyedi nevet az aria-label viszi). */}
      <span className="text-sm font-medium leading-none">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </span>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {renderControl('hu', nameHu)}
        {renderControl('en', nameEn)}
      </div>
    </div>
  );
}
